import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { RouterProvider } from "react-router/dom";

import { GuestOnly, RequireAuth } from "@/features/auth/guards";
import { AppShell } from "@/layouts/app-shell";
import { FullScreenLoader } from "@profitlens/ui/spinner";
import { LandingPage } from "@/pages/landing-page";
import { HelpPage } from "@/pages/help-page";
import { NotFoundPage } from "@/pages/not-found-page";

// Route-level code splitting: keeps zod / react-hook-form (auth + settings) and the
// heavier page trees out of the initial bundle.
const LoginPage = lazy(() =>
  import("@/features/auth/login-page").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/features/auth/register-page").then((m) => ({ default: m.RegisterPage })),
);
const DashboardPage = lazy(() =>
  import("@/pages/dashboard-page").then((m) => ({ default: m.DashboardPage })),
);
const SkuPage = lazy(() => import("@/pages/sku-page").then((m) => ({ default: m.SkuPage })));
const AdsPage = lazy(() => import("@/pages/ads-page").then((m) => ({ default: m.AdsPage })));
const CustomersPage = lazy(() =>
  import("@/pages/customers-page").then((m) => ({ default: m.CustomersPage })),
);
const EmailsPage = lazy(() =>
  import("@/pages/emails-page").then((m) => ({ default: m.EmailsPage })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings-page").then((m) => ({ default: m.SettingsPage })),
);
const SourcesPage = lazy(() =>
  import("@/pages/sources-page").then((m) => ({ default: m.SourcesPage })),
);
const ReportsPage = lazy(() =>
  import("@/pages/reports-page").then((m) => ({ default: m.ReportsPage })),
);
const BillingPage = lazy(() =>
  import("@/pages/billing-page").then((m) => ({ default: m.BillingPage })),
);

/**
 * Everything behind /app requires a session; "/" is the landing/redirect entry.
 * Tauri (S12) can swap `createBrowserRouter` for `createHashRouter` without touching pages.
 */
const router = createBrowserRouter([
  { path: "/", element: <LandingPage /> },
  // 公开帮助中心（内容来自官网管理后台，仅已发布文档）
  { path: "/help", element: <HelpPage /> },
  { path: "/help/:slug", element: <HelpPage /> },
  {
    path: "/login",
    element: (
      <GuestOnly>
        <LoginPage />
      </GuestOnly>
    ),
  },
  {
    path: "/register",
    element: (
      <GuestOnly>
        <RegisterPage />
      </GuestOnly>
    ),
  },
  {
    path: "/app",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/app/dashboard" replace /> },
      { path: "dashboard", element: <DashboardPage /> },
      { path: "sources", element: <SourcesPage /> },
      { path: "sku", element: <SkuPage /> },
      { path: "ads", element: <AdsPage /> },
      { path: "customers", element: <CustomersPage /> },
      { path: "emails", element: <EmailsPage /> },
      { path: "reports", element: <ReportsPage /> },
      { path: "billing", element: <BillingPage /> },
      { path: "settings", element: <SettingsPage /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

export function AppRouter() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
